import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Demo mode orchestrator hook
 * Coordinates auto-play demo across Projects → Project Details → Monitoring
 */
export function useDemoMode() {
  const STEP_DURATION_MS = 30000;
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);
  const [projectsToDemo, setProjectsToDemo] = useState([]);
  const timerRef = useRef(null);
  const controlsRef = useRef({});

  // Register control functions from child components
  const registerControls = useCallback((key, controls) => {
    // Merge controls into one flat map so the runner can call them directly.
    // (We keep `key` only for readability / future debugging.)
    controlsRef.current = {
      ...controlsRef.current,
      ...(controls || {}),
    };
  }, []);

  const smoothScroll = useCallback((element, direction = 'down', durationMs = 3000) => {
    if (!element) return Promise.resolve();
    return new Promise((resolve) => {
      const startTop = element.scrollTop;
      const targetTop = direction === 'down' 
        ? element.scrollHeight - element.clientHeight
        : 0;
      const distance = targetTop - startTop;
      const startTime = performance.now();

      function animate(currentTime) {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / durationMs, 1);
        // Ease in-out
        const ease = progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        
        element.scrollTop = startTop + distance * ease;

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      }

      requestAnimationFrame(animate);
    });
  }, []);

  const executeStep = useCallback(async (step, projectId) => {
    const controls = controlsRef.current;
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    try {
      switch (step) {
        case 0: // Start with map view on the Projects page
          if (controls.setPage) controls.setPage(1);
          if (controls.setViewMode) controls.setViewMode('map');
          await delay(1000);
          // Scroll down to show the map fully (it's at bottom of page)
          const mapContainer = document.querySelector('[data-demo-id="map-view"]');
          if (mapContainer) {
            mapContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          await delay(2000);
          break;

        case 1: // Select project on map - dramatic zoom into project
          console.log('[Demo] Step 1: Focus project on map:', projectId);
          console.log('[Demo] Available controls:', Object.keys(controls));
          if (controls.focusProjectOnMap) {
            console.log('[Demo] Calling focusProjectOnMap...');
            // Zoom in gradually over 35 seconds to moderate zoom level (zoom 10)
            const success = await controls.focusProjectOnMap(projectId, { targetZoom: 10, durationMs: 35000 });
            console.log('[Demo] focusProjectOnMap result:', success);
          } else {
            console.warn('[Demo] focusProjectOnMap not available');
            await delay(1200);
          }
          // Hold at zoomed-in view for a moment before opening details
          await delay(2000);
          if (controls.handleViewProjectDetails) {
            console.log('[Demo] Opening project details...');
            controls.handleViewProjectDetails(projectId);
          }
          await delay(1500);
          break;

        case 2: // In project details, go to Network tab and expand
          if (controls.setActiveTab) controls.setActiveTab(0);
          await delay(1000);
          if (controls.expandAllNetwork) controls.expandAllNetwork();
          await delay(1000);
          // Auto-scroll down in Network section
          const networkSection = document.querySelector('[data-demo-id="network-section"]');
          if (networkSection) {
            await smoothScroll(networkSection, 'down', 4000);
          }
          await delay(2000);
          break;

        case 3: // Go to LMS tab and expand
          if (controls.setActiveTab) controls.setActiveTab(1);
          await delay(1000);
          if (controls.expandAllLms) controls.expandAllLms();
          await delay(1000);
          // Auto-scroll down in LMS section
          const lmsSection = document.querySelector('[data-demo-id="lms-section"]');
          if (lmsSection) {
            await smoothScroll(lmsSection, 'down', 4000);
          }
          await delay(2000);
          break;

        case 4: // Go to Alerts tab and click first alert
          if (controls.setActiveTab) controls.setActiveTab(2);
          await delay(1500);
          if (controls.openFirstAlert) {
            controls.openFirstAlert();
          }
          await delay(1000);
          // Auto-scroll in alert details
          const alertDialog = document.querySelector('[data-demo-id="alert-details-content"]');
          if (alertDialog) {
            await smoothScroll(alertDialog, 'down', 3000);
          }
          await delay(2000);
          if (controls.closeAlertDetails) controls.closeAlertDetails();
          await delay(500);
          break;

        case 5: // Close project details
          if (controls.closeProjectDetails) controls.closeProjectDetails();
          await delay(1000);
          break;

        default:
          break;
      }
      // Hold each step on screen for ~1 minute
      await delay(STEP_DURATION_MS);
    } catch (err) {
      console.error('Demo step error:', err);
    }
  }, [smoothScroll]);

  const startDemo = useCallback((projects) => {
    if (!projects || projects.length === 0) return;
    setProjectsToDemo(projects);
    setCurrentProjectIndex(0);
    setCurrentStep(0);
    setIsActive(true);
  }, []);

  const stopDemo = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
    setCurrentProjectIndex(0);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Demo orchestrator
  useEffect(() => {
    if (!isActive || projectsToDemo.length === 0) return;

    const runDemo = async () => {
      const projectId = projectsToDemo[currentProjectIndex];
      
      // Run current step
      await executeStep(currentStep, projectId);

      // Move to next step
        if (currentStep < 5) {
          // Stay on same project, next step
          timerRef.current = setTimeout(() => {
            setCurrentStep((s) => s + 1);
          }, 500);
      } else {
        // Move to next project
        if (currentProjectIndex < projectsToDemo.length - 1) {
          timerRef.current = setTimeout(() => {
            setCurrentProjectIndex((i) => i + 1);
            setCurrentStep(0);
          }, 1000);
        } else {
          // All projects done, go to Monitoring view
          if (controlsRef.current.setPage) {
            controlsRef.current.setPage(2);
          }
          setTimeout(() => {
            if (controlsRef.current.openFirstMonitoringAlert) {
              controlsRef.current.openFirstMonitoringAlert();
            }
            setTimeout(() => {
              const monitoringAlertDialog = document.querySelector('[data-demo-id="monitoring-alert-details"]');
              if (monitoringAlertDialog) {
                smoothScroll(monitoringAlertDialog, 'down', 4000);
              }
              setTimeout(() => {
                if (controlsRef.current.closeMonitoringAlertDetails) {
                  controlsRef.current.closeMonitoringAlertDetails();
                }
                // End demo
                setTimeout(() => stopDemo(), STEP_DURATION_MS);
              }, STEP_DURATION_MS);
            }, STEP_DURATION_MS);
          }, STEP_DURATION_MS);
        }
      }
    };

    runDemo();

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isActive, currentStep, currentProjectIndex, projectsToDemo, executeStep, stopDemo, smoothScroll]);

  return {
    isActive,
    currentStep,
    currentProjectIndex,
    startDemo,
    stopDemo,
    registerControls,
  };
}
